import { enumIncludes, forEach, forOwn, } from './../utils';
import { ShopError } from './../internals/errors';
// Reservator
import { ReservationType, } from './reservator';
// Item
import { addMetadataFromProduct, CartItemChildType, CartItemType, } from '.';
// Metadata
import { MetadataAppliesTo, } from './metadata';
/**
 *  Used to extend to and create a new typed cart item instance that holds all shared data for the state of the cart item.
 */
class CartItem {
    /**
     *  Constructing a new cart item creates the basic cart item instance,
     *  this should (and can) only be used by calling `super()` in an inherited constructor
     *
     *  #### Possible errors
     *  One type of error could be thrown while initializing a cart item.
     *
     *  | Error                                    | Reason                                                                          |
     *  | :--------------------------------------- | :------------------------------------------------------------------------------ |
     *  | [ShopError](shoperror.html)              | A (child) reservation is not recognized [Reservation](../interfaces/cart.html)  |
     */
    constructor(data, item, reservation, childOf, seatInfo) {
        /**
         *  Optional parent id
         *
         *  @category Cart
         */
        this.childOf = undefined;
        /**
         *  Optional parent item id
         *
         *  @category Cart
         */
        this.childOfItemId = undefined;
        /**
         *  The children of the cart item.
         *  E.g.: (Optional) products in a ticket
         *
         *  @category Cart
         */
        this.children = {};
        /**
         *  The metadata (answers) for the current level of cart item(s)
         *
         *  @category Cart
         */
        this.metadata = {};
        this.metadataList = [];
        this.data = data;
        this.item = item;
        this.reservation = reservation;
        if (childOf) {
            this.childOf = childOf.id;
            this.childOfItemId = childOf.item.guid;
        }
        if (seatInfo) {
            this.seatsInfo = seatInfo;
        }
        this.id = this._setId();
        // Ensure the children collection exists for holding his
        // child reservations (products) in case of a cart ticket
        if (CartTicket.isCartTicket(this)) {
            this.children[CartItemChildType.Products] = {
                map: {},
                collection: [],
            };
        }
        // Process any child reservations if present
        this._processChildReservations(reservation.childReservations);
    }
    /**
     *  Recompute the unique (temporary) identifier for the cart item
     */
    _setId() {
        return this.id = `${this.item.guid}-${this.reservation.reservation}`;
    }
    /**
     *  Determine if the given object is a cart item.
     *  Optionally a (specific) type can be specified
     */
    static isCartItem(cartItem, type) {
        if (!cartItem || typeof cartItem.getType !== 'function') {
            return false;
        }
        if (type) {
            return cartItem.getType() === type;
        }
        return enumIncludes(CartItemType, cartItem.getType());
    }
    /**
     *  Process any child reservations
     *
     *  Child reservations are nested reservation in a reservation response.
     *  E.g.: When reserving a ticket, the API might decide that required products should also be returned.
     *  These products then don't have to be reserved separately (which they would have to be, as they are required).
     *  This also holds for (soft required) product groups.
     *  When a product group has a minimum, returning a 'best guess' child reservation
     *  will (on average) limit the reservation calls.
     *
     *  #### Possible errors
     *  One type of error could be thrown while processing product child reservations.
     *
     *  | Error                                    | Reason                                                                         |
     *  | :--------------------------------------- | :----------------------------------------------------------------------------- |
     *  | [ShopError](shoperror.html)              | A (child) reservation is not recognized [Reservation](../interfaces/cart.html) |
     */
    _processChildReservations(childReservations) {
        if (!childReservations) {
            return {};
        }
        // Only products are currently processed, even though the structure supports other child-cart-items.
        // (i) Once others are added to the backend it should be fairly easy to implement here as well
        const productChildReservations = childReservations[ReservationType.Product] || {};
        forOwn(productChildReservations, (reservations, childId) => {
            this._processProductChildReservation(childId, ...reservations);
        });
        // When the child reservation response holds any unsupported reservation types,
        // an error is logged to the console to mitigate hard to debug behaviour
        forEach(Object.keys(childReservations), (reservationType) => {
            // Currently only products are supported, use the following check if also tickets are supported
            // !enumIncludes<string>(ReservationType, reservationType)
            if (reservationType !== ReservationType.Product &&
                Object.prototype.hasOwnProperty.call(childReservations, reservationType)) {
                // TODO: Should we log this some other way, instead / in addition ??
                // eslint-disable-next-line no-console
                console.error(`Child reservation of type ${reservationType} ignored`);
            }
        });
        return this.children;
    }
    /**
     *  Add the product cart items for the given id and (child) reservations
     *
     *  #### Possible errors
     *  One type of error could be thrown while processing product child reservations.
     *
     *  | Error                                    | Reason                                                                         |
     *  | :--------------------------------------- | :----------------------------------------------------------------------------- |
     *  | [ShopError](shoperror.html)              | A (child) reservation is not recognized [Reservation](../interfaces/cart.html) |
     */
    _processProductChildReservation(childId, ...reservations) {
        // When the childId is invalid, the (child) reservations can not be added.
        if (!Object.prototype.hasOwnProperty.call(this.data.products_map, childId)) {
            throw new ShopError(`Unrecognized child reservation, invalid guid ${childId}`);
        }
        const product = this.data.products_map[childId];
        // Because we are processing child reservations here we need to ensure there is a collection for them
        const childCollection = this.children[CartItemChildType.Products] = this.children[CartItemChildType.Products] || {
            map: {},
            collection: [],
        };
        // Also save the added cart products in an Array to return, so
        // we only return the now created/added child products
        const addedChildren = [];
        // Ensure the (reserved) product's map exist in the children
        // collection to be able to add it to the map below
        childCollection.map[childId] = childCollection.map[childId] || {};
        // Create a cart product for every child (reservation) and add it to the children collection(s)
        forEach(reservations, (reservation) => {
            const cartItem = new CartProduct(this.data, product, reservation, this);
            // Add the item to both the map and the collection
            childCollection.map[childId][cartItem.id] = cartItem;
            childCollection.collection.push(cartItem);
            addedChildren.push(cartItem);
        });
        return addedChildren;
    }
}
/**
 *  Used to create a new ticket cart item instance that holds all data for the state of the ticket cart item.
 */
class CartTicket extends CartItem {
    /**
     *  Constructing a new ticket cart item creates the cart item instance.
     *
     *  When product child reservations are included, these products can contain
     *  metadata that should be applied on the ticket.
     *  Those (cart) metadata are added to the ticket iff they are not present yet.
     *  If the metadata is already present (e.g.: because multiple products add the same metadata),
     *  it will be ignored. The alternative would be overwriting existing metadata,
     *  which could result in losing already filled in values.
     *
     *  #### Possible errors
     *  One type of error could be thrown while initializing a ticket cart item.
     *
     *  | Error                                    | Reason                                                                          |
     *  | :--------------------------------------- | :------------------------------------------------------------------------------ |
     *  | [ShopError](shoperror.html)              | A (child) reservation is not recognized [Reservation](../interfaces/cart.html)  |
     */
    constructor(data, ticket, reservation, childOf, seatInfo) {
        super(data, ticket, reservation, childOf, seatInfo);
        // Work with a copy of the original pricing in the cart to not alter the
        // item's original pricing, as coupons get applied on this pricing object
        this.pricing = JSON.parse(JSON.stringify(Object.assign(Object.assign({}, ticket.pricing), { coupons: {}, coupons_count: {} })));
        // Create a shortcut to the cart ticket's products (which are
        // his children), which have been created/set in his super
        this.products = this.children[CartItemChildType.Products];
        // Save his original min price as it's needed for recomputing his pricing
        this.min_price = this.item.min_price;
        // Only add the metadata if the ticket's personalization should
        // not be later, because the buyer doesn't need to enter the
        // metadata now if it should be personalized later
        forOwn(this.products.map, (products) => {
            forOwn(products, (product) => {
                if (!ticket.late_personalization || product.skip_late_personalization) {
                    addMetadataFromProduct(product.item, this.metadata, this.metadataList, MetadataAppliesTo.Person);
                }
            });
        });
    }
    /**
     *  Retrieve the cart item type
     */
    getType() {
        return CartItemType.Ticket;
    }
    /**
     *  Determine if the given object is a cart ticket
     */
    static isCartTicket(cartItem) {
        return CartItem.isCartItem(cartItem, CartItemType.Ticket);
    }
    /**
     *  Remove any metadata no longer used by the global products in the cart.
     */
    _checkRemovedProductMetadata(metadataCollection) {
        const metadataRemove = {};
        forEach(metadataCollection, (metadata) => {
            if (metadata.item.applies_to !== MetadataAppliesTo.Person) {
                return;
            }
            metadataRemove[metadata.guid] = metadata;
        });
        // If there are multiple (active) roads to Rome, Rome should not be destroyed.
        // In this case other products.
        forOwn(this.products.map, (cartProducts) => {
            forOwn(cartProducts, (cartProduct) => {
                forEach(cartProduct.item.metadata, (metadata) => {
                    // Ignore product metadata
                    if (metadata.item.applies_to !== MetadataAppliesTo.Person) {
                        return;
                    }
                    delete metadataRemove[metadata.guid];
                });
            });
        });
        // Actually remove the metadata from the cart
        forOwn(metadataRemove, (metadata, metadataId) => {
            delete this.metadata[metadataId];
            const listIndex = this.metadataList.findIndex((cartMetadata) => cartMetadata.item.guid === metadata.guid);
            if (listIndex > -1) {
                this.metadataList.splice(listIndex, 1);
            }
        });
        // TODO Emit ???
    }
}
/**
 *  Used to create a new product cart item instance that holds all data for the state of the product cart item.
 */
class CartProduct extends CartItem {
    /**
     *  Constructing a new product cart item creates the cart item instance,
     *
     *  A product can have metadata that is applied to the product itself
     *  or on the parent (e.g.: ticket).
     *  Here only the metadata applied to the product is processed.
     *  Any metadata applied to the parent will be handled by the parent
     *  when adding this cart product to the parent.
     *
     *  #### Possible errors
     *  One type of error could be thrown while initializing a product cart item.
     *
     *  | Error                                    | Reason                                                                          |
     *  | :--------------------------------------- | :------------------------------------------------------------------------------ |
     *  | [ShopError](shoperror.html)              | A (child) reservation is not recognized [Reservation](../interfaces/cart.html)  |
     */
    constructor(data, product, reservation, childOf) {
        super(data, product, reservation, childOf);
        // If the product is optional for the ticket to which it's added,
        // which defaults to true if it doesn't belong to a ticket
        this.is_optional = true;
        // Boolean wheter to force show metadata in shop, even when late_personalization is on
        this.skip_late_personalization = false;
        // Work with a copy of the original pricing in the cart to not alter the
        // item's original pricing, as coupons get applied on this pricing object
        this.pricing = JSON.parse(JSON.stringify(product.pricing));
        this.skip_late_personalization = product.skip_late_personalization;
        // Set the optional state of the product
        this.setOptional();
        // Only add the metadata if it's about a global product or
        // if the product's ticket should not be personalized later
        const addMetadata = !childOf || (CartTicket.isCartTicket(childOf) && !childOf.item.late_personalization) || this.skip_late_personalization;
        if (addMetadata) {
            addMetadataFromProduct(product, this.metadata, this.metadataList, MetadataAppliesTo.Product);
        }
    }
    /**
     *  Used to compute the optional state of a cart product. It is always optional
     *  by default, but becomes required if the product is part of a ticket and it's
     *  located in the tickets' required products collection.
     */
    setOptional() {
        this.is_optional = true;
        if (!this.childOfItemId) {
            return;
        }
        const ticket = this.data.tickets_map[this.childOfItemId];
        if (!ticket) {
            return;
        }
        forEach(ticket.products.required, (ticketProduct) => {
            if (ticketProduct.item.guid === this.item.guid) {
                return this.is_optional = false;
            }
        });
    }
    /**
     *  Retrieve the cart item type
     */
    getType() {
        return CartItemType.Product;
    }
    /**
     *  Determine if the given object is a cart product
     */
    static isCartProduct(cartItem) {
        return CartItem.isCartItem(cartItem, CartItemType.Product);
    }
}
export { CartItem, CartTicket, CartProduct, };
//# sourceMappingURL=item.js.map